import { streamingRemoteFeedback, streamingResponseText, streamingFieldText, streamingFeedbackText } from "../i18n/streamingFeedbackPresentation";
import { type StreamingMessage, useStreamingLanguage, streamMessage, streamException, streamLocal, streamFeedback, streamText } from "../i18n/streamingDashboardPresentation";
/**
 * useStreamingDevices -- React hook for fetching all WSD-tracked devices.
 *
 * Endpoint: GET /api/streaming/devices
 *
 * Returns the full device list across all configured servers (Emby/Jellyfin).
 * Also provides mutation hooks for rename and delete operations.
 */
import { useCallback, useEffect, useRef, useState } from "react";

import { getCsrfToken } from "../lib/csrf";

// ============================================================================
// Types (mirrored from backend ExtendedDeviceInfo / toDeviceInfo)
// ============================================================================

export interface StreamingDevice {
  serverType: string;
  username: string;
  deviceId: string;
  deviceName: string;
  customName: string | null;
  platform: string | null;
  clientVersion: string | null;
  userAgent: string | null;
  ipAddress: string | null;
  isActive: boolean;
  isLocked?: boolean;
  firstSeen: number;
  lastSeen: number;
  connectionCount: number;
  /**
   * Matched upstream media-server user id for deep-linking the device's owner to
   * their server-user page. Null when the username could not be matched upstream.
   */
  userId?: string | null;
}

// ============================================================================
// Cache
// ============================================================================

interface CachedDevices {
  devices: StreamingDevice[];
  fetchedAt: number;
}

const devicesCache = new Map<string, CachedDevices>();
const CACHE_TTL_MS = 2 * 60 * 1000; // 2 minutes
const CACHE_KEY = "streaming-devices-all";

// ============================================================================
// useStreamingDevices
// ============================================================================

export function useStreamingDevices(enabled: boolean = false): {
  devices: StreamingDevice[] | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
} {
  const [devices, setDevices] = useState<StreamingDevice[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorFeedback, setError] = useState<string | StreamingMessage | null>(null);
  useStreamingLanguage();
  const error = streamMessage(errorFeedback);
  const abortRef = useRef<AbortController | null>(null);
  const fetchedKeyRef = useRef<string | null>(null);
  const isFetchingRef = useRef(false);

  const fetchDevices = useCallback(
    async (force?: boolean) => {
      if (!enabled) return;

      // Check cache (unless forced)
      if (!force) {
        const cached = devicesCache.get(CACHE_KEY);
        if (cached && Date.now() - cached.fetchedAt < CACHE_TTL_MS) {
          setDevices(cached.devices);
          setError(null);
          fetchedKeyRef.current = CACHE_KEY;
          return;
        }
      }

      // Skip if already fetching
      if (isFetchingRef.current && fetchedKeyRef.current === CACHE_KEY) return;

      // Abort any in-flight request
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      isFetchingRef.current = true;
      setIsLoading(true);
      setError(null);

      try {
        const res = await fetch("/api/streaming/devices", { signal: controller.signal });
        const data = await res.json();

        if (!res.ok || !data.ok || !data.devices) {
          throw streamException((streamingRemoteFeedback(data) ?? data.message ?? streamLocal("failedToLoadDevices")));
        }

        const fetchedDevices = data.devices as StreamingDevice[];
        setDevices(fetchedDevices);
        setError(null);

        // Cache
        devicesCache.set(CACHE_KEY, {
          devices: fetchedDevices,
          fetchedAt: Date.now(),
        });
        fetchedKeyRef.current = CACHE_KEY;
      } catch (err) {
        if (err instanceof DOMException && err.name === "AbortError") return;
        const message = streamFeedback(err, "failedToLoadDevices");
        setError(message);
      } finally {
        isFetchingRef.current = false;
        setIsLoading(false);
      }
    },
    [enabled]
  );

  // Fetch on mount or when enabled changes
  useEffect(() => {
    if (!enabled) {
      setDevices(null);
      setError(null);
      fetchedKeyRef.current = null;
      return;
    }

    if (fetchedKeyRef.current !== CACHE_KEY) {
      void fetchDevices();
    }

    return () => {
      abortRef.current?.abort();
    };
  }, [enabled, fetchDevices]);

  const refetch = useCallback(() => {
    // Invalidate cache on manual refetch
    devicesCache.delete(CACHE_KEY);
    void fetchDevices(true);
  }, [fetchDevices]);

  return { devices, isLoading, error, refetch };
}

// ============================================================================
// useDeviceRename
// ============================================================================

export function useDeviceRename(): {
  renaming: boolean;
  rename: (
    serverType: string,
    username: string,
    deviceId: string,
    customName: string | null,
    syncUpstream?: boolean,
    deviceName?: string
  ) => Promise<{ device: StreamingDevice; upstreamSynced: boolean; upstreamError?: string } | null>;
} {
  const [renaming, setRenaming] = useState(false);

  const rename = useCallback(
    async (
      serverType: string,
      username: string,
      deviceId: string,
      customName: string | null,
      syncUpstream: boolean = true,
      deviceName?: string
    ): Promise<{ device: StreamingDevice; upstreamSynced: boolean; upstreamError?: string } | null> => {
      setRenaming(true);
      try {
        const csrfToken = getCsrfToken();
        const url = `/api/streaming/devices/${encodeURIComponent(serverType)}/${encodeURIComponent(username)}/${encodeURIComponent(deviceId)}/rename`;

        const res = await fetch(url, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            ...(csrfToken ? { "x-csrf-token": csrfToken } : {}),
          },
          body: JSON.stringify({
            customName,
            syncUpstream,
            ...(deviceName ? { deviceName } : {}),
          }),
        });

        const json = await res.json();

        if (!res.ok || !json.ok || !json.device) {
          throw streamException((streamingRemoteFeedback(json) ?? json.message ?? streamLocal("failedToRenameDevice")));
        }

        // Invalidate cache
        devicesCache.delete(CACHE_KEY);

        return {
          device: json.device as StreamingDevice,
          upstreamSynced: json.upstreamSynced ?? false,
          upstreamError: json.upstreamError,
        };
      } catch (err) {
        const message = streamFeedback(err, "failedToRenameDevice");
        throw streamException(message);
      } finally {
        setRenaming(false);
      }
    },
    []
  );

  return { renaming, rename };
}

// ============================================================================
// useDeviceDelete
// ============================================================================

export function useDeviceDelete(): {
  deleting: boolean;
  deleteDevice: (
    serverType: string,
    username: string,
    deviceId: string
  ) => Promise<boolean>;
} {
  const [deleting, setDeleting] = useState(false);

  const deleteDevice = useCallback(
    async (
      serverType: string,
      username: string,
      deviceId: string
    ): Promise<boolean> => {
      setDeleting(true);
      try {
        const csrfToken = getCsrfToken();
        const url = `/api/streaming/devices/${encodeURIComponent(serverType)}/${encodeURIComponent(username)}/${encodeURIComponent(deviceId)}`;

        const res = await fetch(url, {
          method: "DELETE",
          headers: {
            ...(csrfToken ? { "x-csrf-token": csrfToken } : {}),
          },
        });

        const json = await res.json();

        if (!res.ok || !json.ok) {
          throw streamException((streamingRemoteFeedback(json) ?? json.message ?? streamLocal("failedToDeleteDevice")));
        }

        // Invalidate cache
        devicesCache.delete(CACHE_KEY);

        return true;
      } catch (err) {
        const message = streamFeedback(err, "failedToDeleteDevice");
        throw streamException(message);
      } finally {
        setDeleting(false);
      }
    },
    []
  );

  return { deleting, deleteDevice };
}

// ============================================================================
// useDeviceLock
// ============================================================================

export function useDeviceLock(): {
  locking: boolean;
  lockDevice: (
    serverType: string,
    username: string,
    deviceId: string,
    locked: boolean
  ) => Promise<boolean>;
} {
  const [locking, setLocking] = useState(false);

  const lockDevice = useCallback(
    async (
      serverType: string,
      username: string,
      deviceId: string,
      locked: boolean
    ): Promise<boolean> => {
      setLocking(true);
      try {
        const csrfToken = getCsrfToken();
        const url = `/api/streaming/devices/${encodeURIComponent(serverType)}/${encodeURIComponent(username)}/${encodeURIComponent(deviceId)}/lock`;

        const res = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(csrfToken ? { "x-csrf-token": csrfToken } : {}),
          },
          body: JSON.stringify({ locked }),
        });

        const json = await res.json();

        if (!res.ok || !json.ok) {
          throw streamException((streamingRemoteFeedback(json) ?? json.message ?? streamLocal("failedToUpdateDeviceLock")));
        }

        // Invalidate cache
        devicesCache.delete(CACHE_KEY);

        return true;
      } catch (err) {
        const message = streamFeedback(err, "failedToUpdateDeviceLock");
        throw streamException(message);
      } finally {
        setLocking(false);
      }
    },
    []
  );

  return { locking, lockDevice };
}

// ============================================================================
// useDeviceNameSync
// ============================================================================

export interface DeviceSyncResult {
  deviceId: string;
  serverType: string;
  username: string;
  deviceName: string;
  success: boolean;
  error?: string;
}

export interface DeviceSyncResponse {
  total: number;
  synced: number;
  failed: number;
  skipped: number;
  results: DeviceSyncResult[];
}

export function useDeviceNameSync(): {
  syncing: boolean;
  syncNames: () => Promise<DeviceSyncResponse | null>;
} {
  const [syncing, setSyncing] = useState(false);

  const syncNames = useCallback(async (): Promise<DeviceSyncResponse | null> => {
    setSyncing(true);
    try {
      const csrfToken = getCsrfToken();
      const res = await fetch("/api/streaming/devices/sync-names", {
        method: "POST",
        headers: {
          ...(csrfToken ? { "x-csrf-token": csrfToken } : {}),
        },
      });

      const json = await res.json();

      if (!res.ok || !json.ok) {
        throw streamException((streamingRemoteFeedback(json) ?? json.message ?? streamLocal("failedToSyncDeviceNames")));
      }

      // Invalidate cache so next fetch reflects any upstream changes
      devicesCache.delete(CACHE_KEY);

      return {
        total: json.total as number,
        synced: json.synced as number,
        failed: json.failed as number,
        skipped: json.skipped as number,
        results: json.results as DeviceSyncResult[],
      };
    } catch (err) {
      const message = streamFeedback(err, "failedToSyncDeviceNames");
      throw streamException(message);
    } finally {
      setSyncing(false);
    }
  }, []);

  return { syncing, syncNames };
}

// ============================================================================
// Stale device candidates (review surface)
// Endpoint: GET /api/streaming/devices/stale-candidates
// ============================================================================

export interface StaleDeviceCandidate {
  serverType: string;
  deviceId: string;
  deviceName: string;
  /** Last known user — also used as the DELETE path `username` fallback. */
  lastUserName?: string;
  /** ISO-8601 timestamp of last activity, when known. */
  lastActivity?: string;
  /** Whole days inactive, when known. */
  inactiveDays?: number;
  /** true ONLY for Plex — removal affects the whole Plex account. */
  accountWide: boolean;
}

export interface StaleServerDiagnostic {
  serverType: string;
  scanned: boolean;
  deviceCount: number;
  activeCount: number;
  candidateCount: number;
  error?: string;
}

export interface StaleCandidatesResult {
  thresholdDays: number;
  candidates: StaleDeviceCandidate[];
  servers: StaleServerDiagnostic[];
}

export function useStaleDeviceCandidates(): {
  loading: boolean;
  error: string | null;
  result: StaleCandidatesResult | null;
  scan: (thresholdDays?: number, serverType?: string) => Promise<StaleCandidatesResult | null>;
  reset: () => void;
} {
  const [loading, setLoading] = useState(false);
  const [errorFeedback, setError] = useState<string | StreamingMessage | null>(null);
  useStreamingLanguage();
  const error = streamMessage(errorFeedback);
  const [result, setResult] = useState<StaleCandidatesResult | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const scan = useCallback(
    async (thresholdDays?: number, serverType?: string): Promise<StaleCandidatesResult | null> => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams();
        if (thresholdDays != null) params.set("thresholdDays", String(thresholdDays));
        if (serverType) params.set("serverType", serverType);
        const qs = params.toString();
        const url = `/api/streaming/devices/stale-candidates${qs ? `?${qs}` : ""}`;

        const res = await fetch(url, { signal: controller.signal });
        const json = await res.json();

        if (!res.ok || !json.ok || !json.data) {
          throw streamException((streamingRemoteFeedback(json) ?? json.message ?? streamLocal("failedToScanForStaleDevices")));
        }

        const data = json.data as StaleCandidatesResult;
        setResult(data);
        return data;
      } catch (err) {
        if (err instanceof DOMException && err.name === "AbortError") return null;
        const message = streamFeedback(err, "failedToScanForStaleDevices");
        setError(message);
        return null;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const reset = useCallback(() => {
    abortRef.current?.abort();
    setResult(null);
    setError(null);
    setLoading(false);
  }, []);

  return { loading, error, result, scan, reset };
}

// ============================================================================
// Hard upstream device removal (destructive — removes the device registration
// from the media server itself, not just the local WSD record).
// Endpoint: DELETE /api/streaming/devices/:serverType/:username/:deviceId/upstream
// ============================================================================

export interface UpstreamRemovalResult {
  serverType: string;
  username: string;
  deviceId: string;
  accountWide: boolean;
  /** True when the device was already absent upstream (idempotent removal). */
  alreadyGone: boolean;
  /** Backend success discriminator: "removed" | "already_gone". */
  code: string;
  message: string;
}

/**
 * Maps a non-ok upstream-removal response to a user-facing error message.
 * Prefers the backend `message`, but synthesizes clearer copy for the known
 * `code` discriminators when the server gave a terse message.
 */
function removalErrorMessage(
  json: { ok?: boolean; code?: unknown; message?: unknown },
  serverType: string
): string | StreamingMessage {
  const code = typeof json.code === "string" ? json.code : undefined;
  const serverLabel = serverTypeLabel(serverType);
  if (code === "no_server") {
    return typeof json.message === "string" && json.message.length > 0
      ? streamingResponseText(json, json.message)
      : streamLocal("noServerConfigured", { server: serverLabel });
  }
  if (code === "upstream_error") {
    return typeof json.message === "string" && json.message.length > 0
      ? streamingResponseText(json, json.message)
      : streamLocal("removalRejected");
  }
  return typeof json.message === "string" && json.message.length > 0
    ? streamingResponseText(json, json.message)
    : streamLocal("deviceRemovalFailed");
}

/** Human-friendly server label for inline error copy. */
function serverTypeLabel(serverType: string): string {
  switch (serverType) {
    case "emby":
      return "Emby";
    case "jellyfin":
      return "Jellyfin";
    case "plex":
      return "Plex";
    default:
      return serverType;
  }
}

export function useDeviceUpstreamRemoval(): {
  removing: boolean;
  removeUpstream: (
    serverType: string,
    username: string,
    deviceId: string
  ) => Promise<UpstreamRemovalResult>;
} {
  const [removing, setRemoving] = useState(false);

  const removeUpstream = useCallback(
    async (
      serverType: string,
      username: string,
      deviceId: string
    ): Promise<UpstreamRemovalResult> => {
      setRemoving(true);
      try {
        const csrfToken = getCsrfToken();
        const url = `/api/streaming/devices/${encodeURIComponent(serverType)}/${encodeURIComponent(username)}/${encodeURIComponent(deviceId)}/upstream`;

        const res = await fetch(url, {
          method: "DELETE",
          headers: {
            ...(csrfToken ? { "x-csrf-token": csrfToken } : {}),
          },
        });

        const json = await res.json();

        // Discriminated outcome contract: the endpoint no longer
        // emits 404. Switch on the parsed body — `json.ok === true` is the ONLY
        // success signal (both "removed" and "already_gone"); everything else is
        // a genuine error (`no_server` / `upstream_error` / blank deviceId).
        if (!res.ok || !json.ok || !json.data) {
          throw streamException(removalErrorMessage(json, serverType));
        }

        // Backend also soft-deletes the local record on success — invalidate cache.
        devicesCache.delete(CACHE_KEY);

        const data = json.data as {
          serverType?: string;
          username?: string;
          deviceId?: string;
          accountWide?: boolean;
          alreadyGone?: boolean;
          message?: string;
        };
        const alreadyGone = data.alreadyGone ?? json.code === "already_gone";

        return {
          serverType: data.serverType ?? serverType,
          username: data.username ?? username,
          deviceId: data.deviceId ?? deviceId,
          accountWide: data.accountWide ?? serverType === "plex",
          alreadyGone,
          code: typeof json.code === "string" ? json.code : alreadyGone ? "already_gone" : "removed",
          message:
            streamingResponseText(data, data.message ??
            (alreadyGone
              ? streamText("deviceWasAlreadyRemovedFromTheMediaServer")
              : streamText("deviceRemovedFromTheMediaServer"))),
        };
      } catch (err) {
        const message = streamFeedback(err, "failedToRemoveDeviceFromTheMediaServer");
        throw streamException(message);
      } finally {
        setRemoving(false);
      }
    },
    []
  );

  return { removing, removeUpstream };
}
